import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { catchError, tap, of } from 'rxjs';
import { LoadingService, NotificationService } from 'src/app/core';
import { JobCardService } from 'src/app/modules/job-operation/services/job-card.service';
import { PaginationComponent } from 'src/app/core';
import { JobCardFindDTO, JobCardGetDTO } from 'src/app/modules/job-operation/interfaces/job-card.entity';
import { Department, Machine } from 'src/app/modules/shared/interface/department.entity';
import { DepartmentService } from 'src/app/modules/shared/services/department.service';
import { JobOperationService } from 'src/app/modules/shared/services/job-operation.service';
import { JobOperationItemDTO } from 'src/app/modules/shared/interface/jop-operation.entity';
import { MachineService } from 'src/app/modules/shared/services/machine.service';
import { ModalService } from 'src/app/modules/shared/services/modal.service';
import { GlobalDepartmentService } from 'src/app/core/services/global-department.service';
import * as XLSX from 'xlsx';
import { JobCardDTO } from 'src/app/modules/job-card-upload/interfaces/job.entity';

@Component({
    selector: 'app-operation-create',
    imports: [
        CommonModule,
        FormsModule,
        PaginationComponent
    ],
    templateUrl: './operation-create.component.html',
    styleUrl: './operation-create.component.scss'
})
export class OperationCreateComponent {

    jobCardService = inject(JobCardService);
    notification = inject(NotificationService);
    loading = inject(LoadingService);
    departmentService = inject(DepartmentService);
    jobOperationService = inject(JobOperationService);
    machineService = inject(MachineService);
    modal = inject(ModalService);
    globalDepartmentService = inject(GlobalDepartmentService);

    $$showFileUploadModal = signal(false);

    protected Department = signal<Department[]>([]);
    protected jobCardFindDTOS = signal<JobCardFindDTO[]>([]);
    protected jobCardGetDTOS = signal<JobCardGetDTO[]>([]);
    protected JobOperationItemDTOS = signal<JobOperationItemDTO[]>([]);
    protected $$departmentDTO = signal<Department[]>([]);
    protected $$availableMachines = signal<Machine[]>([]);
    protected $$allMachines = signal<Machine[]>([]);
    protected selectedJobDetails: any = null;
    protected Time: string = ''

    private draggedIndex: number | null = null;
    private dragOverIndex: number | null = null;
    selectedDeliverDate: string = '';
    saveMessage: string | null = null;
    errorMessage = signal<string | null>(null);
    department_id: number | null = null;
    selectedFile: File | null = null;
    selectedFileName: string | null = null;
    errorMessageN: string | null = null;
    departmentId = this.globalDepartmentService.selectedDepartmentId;
    hasSearched = signal(false);
    // saveMessage: string | null = null;


    protected readonly $$totalItems = signal(0);
    protected readonly $$pageNumber = signal(1);
    protected readonly $$itemsPerPage = signal(1);

    selectedYear = signal(new Date().getFullYear());
    selectedMonth = signal(new Date().getMonth());

    calendarDays: Date[] = [];
    months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    years: number[] = [];

    minDate: string = new Date().toISOString().split('T')[0];
    minDateTime: string = new Date().toISOString().slice(0, 16);


    protected searchParams = signal({
        pp_number: '',
        status: 'pending',
        delivery_date: '',
        machine_id: -1,
        department_id: this.globalDepartmentService.getDepartmentId() ?? -1,
        page_number: 1,
        items_per_page: 15,
        request_date: '',
        cs_released_at: ''
    });

    protected searchParamsOp = signal({
        id: 2,
        year: this.selectedYear(),
        month: this.selectedMonth() + 1
    });

    protected finishings = signal<{ name: string; departmentId: number | null; machineId: number | null; endDate: string; endTime: string; availableMachines: Machine[]; estimateTime: number | null; operationQuantity: number | null; remarks: string | null; workOrderId?: string | null; workOrderName?: string | null; isFinal?: boolean }[]>([]);


    constructor() {
        this.jobCardService.getRefreshObservable().subscribe(() => {
            this.fetchPOData();
        });
        this.fetchDepartment();
        this.initializeCurrentWeek();
        this.populateYears();
    }

    populateYears(): void {
        const startYear = 2025;
        this.years = Array.from({ length: 11 }, (_, i) => startYear + i);
    }

    protected startDayIndex = signal(0);

    generateCalendar(): void {
        const year = this.selectedYear();
        const month = this.selectedMonth();
        const daysInMonthMap = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

        if (month === 1) {
            const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
            daysInMonthMap[1] = isLeapYear ? 29 : 28;
        }

        const daysInMonth = daysInMonthMap[month];

        if (this.startDayIndex() + 7 > daysInMonth) {
            this.startDayIndex.set(Math.max(0, daysInMonth - 7));
        }

        this.calendarDays = Array.from(
            { length: 7 },
            (_, i) => new Date(year, month, this.startDayIndex() + i + 1)
        );
    }

    initializeCurrentWeek(): void {
        const today = new Date();
        this.selectedYear.set(today.getFullYear());
        this.selectedMonth.set(today.getMonth());

        const currentDate = today.getDate(); // 1 to 31
        const startIndex = Math.floor((currentDate - 1) / 7) * 7;
        this.startDayIndex.set(startIndex);

        this.generateCalendar();
    }


    previousWeek(): void {
        if (this.startDayIndex() > 0) {
            this.startDayIndex.set(this.startDayIndex() - 7);
            this.generateCalendar();
        }
    }

    nextWeek(): void {
        const daysInMonth = new Date(this.selectedYear(), this.selectedMonth() + 1, 0).getDate();
        if (this.startDayIndex() + 7 < daysInMonth) {
            this.startDayIndex.set(this.startDayIndex() + 7);
            this.generateCalendar();
        }
    }

    get daysInCurrentMonth(): number {
        return new Date(this.selectedYear(), this.selectedMonth() + 1, 0).getDate();
    }

    updateCalendar(): void {
        this.generateCalendar();
    }

    isSameDate(dateString: string, day: Date): boolean {
        const date = new Date(dateString);
        return date.toDateString() === day.toDateString();
    }

    public fetchPOData(): void {
        const params = this.searchParams();

        if (!params.pp_number?.trim()) {
            this.jobCardFindDTOS.set([]);
            this.$$totalItems.set(0);
            this.$$pageNumber.set(1);
            this.hasSearched.set(false);
            return;
        }

        this.hasSearched.set(true);
        this.jobCardService.find(params).pipe(
            tap(response => {
                this.jobCardFindDTOS.set(response.data.data);
                this.$$totalItems.set(response.data.totalItems);
                this.$$pageNumber.set(response.data.page);
                this.$$itemsPerPage.set(response.data.itemsPerPage);
            }),
            catchError(error => {
                this.notification.set({ type: 'error', message: `Failed to fetch PO data` });
                console.error(error);
                return of(null);
            })
        ).subscribe();
    }

    public onPageChange(pageNumber: number): void {
        const updatedParams = {
            ...this.searchParams(),
            page_number: Number(pageNumber)
        };
        this.searchParams.set(updatedParams);
        this.fetchPOData();
    }

    protected getJobDetails(ID: number): void {
        this.$$showFileUploadModal.set(true);
        this.jobCardService.getById(String(ID)).pipe(
            tap(response => {
                this.loading.set(false);
                this.jobCardGetDTOS.set([response.data]);
                console.log(this.jobCardGetDTOS()[0].reelLength);

                this.selectedJobDetails = response.data;
                this.selectedDeliverDate = '';

                const isFlexo = response.data.productType?.toLowerCase().includes('flexo');
                const impressionOrReel = isFlexo
                    ? response.data.reelLength
                    : response.data.numberOfImpressions;

                const globalDeptId = this.globalDepartmentService.getDepartmentId();
                const globalDept = this.$$departmentDTO().find(d => d.departmentId === globalDeptId);
                const defaultMachines = globalDept ? globalDept.machines : [];

                const fetchedFinishings = response.data.workOrders && response.data.workOrders.length > 0
                    ? response.data.workOrders.map((wo: any) => ({
                        name: wo.operation ? wo.operation.trim().toUpperCase() : '',
                        departmentId: globalDeptId,
                        machineId: null,
                        endDate: '',
                        endTime: '',
                        availableMachines: [...defaultMachines] as Machine[],
                        estimateTime: null,
                        operationQuantity: Number(wo.qty) || 0,
                        remarks: null,
                        workOrderId: wo.workOrderId || null,
                        workOrderName: wo.item || null,
                        isFinal: false
                    }))
                    : [];

                const requiredFinishings = [
                    {
                        name: 'BREAKING',
                        operationQuantity: impressionOrReel
                    }
                ];

                requiredFinishings.forEach(req => {
                    if (!fetchedFinishings.some((f: any) => f.name === req.name)) {
                        fetchedFinishings.push({
                            name: req.name,
                            departmentId: globalDeptId,
                            machineId: null,
                            endDate: '',
                            endTime: '',
                            availableMachines: [...defaultMachines] as Machine[],
                            estimateTime: null,
                            operationQuantity: req.operationQuantity,
                            remarks: null,
                            workOrderId: null,
                            workOrderName: null,
                            isFinal: false
                        });
                    }
                });

                this.finishings.set(fetchedFinishings);
            }),

            catchError(error => {
                this.loading.set(false);
                this.notification.set({ type: 'error', message: `Failed to load Purchase Order details` });
                console.error(error);
                return of(null);
            })
        ).subscribe();
    }


    protected get(): void {
        this.loading.set(true)
        this.departmentService.getOperationsByDep(this.searchParamsOp()).pipe(
            tap(response => {
                this.loading.set(false);
                this.Department.set([response.data]);
                //    console.log(this.Department())
            }),
            catchError(error => {
                this.loading.set(false);
                this.notification.set({ type: 'error', message: `Failed to fetch Job Operation data` });
                console.error(error);
                return of(null);
            })
        ).subscribe();
    }

    public fetchDepartment(): void {
        this.departmentService.getAllDepartments().subscribe({
            next: (result) => {
                if (result.data && result.data.departments) {
                    const departments = result.data.departments;
                    this.$$departmentDTO.set(departments);

                    this.$$availableMachines.set([]);

                    const machines = departments.reduce((acc: Machine[], dept: Department) => {
                        return acc.concat(dept.machines);
                    }, []);
                    this.$$allMachines.set(machines);

                }
            },
            error: (error) => {
                console.error('Error fetching departments:', error);
            }
        });
    }

    onDepartmentChange(departmentId: number, index: number): void {
        const selectedDepartment = this.$$departmentDTO().find(
            (dept) => dept.departmentId === Number(departmentId)
        );

        const updatedFinishings = [...this.finishings()];
        updatedFinishings[index].departmentId = departmentId;
        updatedFinishings[index].availableMachines = selectedDepartment ? selectedDepartment.machines : [];
        updatedFinishings[index].machineId = null;
        updatedFinishings[index].endDate = '';
        updatedFinishings[index].estimateTime = null;
        this.finishings.set(updatedFinishings);
    }

    onDepartmentChangeJob(departmentId: number): void {
        const deptId = Number(departmentId);

        this.globalDepartmentService.setDepartmentId(deptId);

        this.searchParams.update(params => ({
            ...params,
            department_id: deptId,
            page_number: 1
        }));

        this.fetchPOData();
    }


    protected selectedPPNo: string | null = null;

    protected onJobRowClick(ppNo: string): void {
        this.selectedPPNo = ppNo;
    }

    onDragStart(index: number): void {
        this.draggedIndex = index;
    }

    onDragOver(event: DragEvent, index: number): void {
        event.preventDefault();
        this.dragOverIndex = index;
    }

    onDrop(index: number): void {
        if (this.draggedIndex === null || this.dragOverIndex === null) return;

        const draggedItem = this.finishings()[this.draggedIndex];
        const updatedFinishings = [...this.finishings()];
        updatedFinishings.splice(this.draggedIndex, 1);
        updatedFinishings.splice(index, 0, draggedItem);

        this.finishings.set(updatedFinishings);
        this.draggedIndex = null;
        this.dragOverIndex = null;
    }

    onDragEnd(): void {
        this.draggedIndex = null;
        this.dragOverIndex = null;
    }

    saveMachineDetails(): void {

        this.loading.set(true);
        if (!this.isFormValid()) {
            this.loading.set(false);
            this.saveMessage = 'Error! Please fill all required fields before saving.';
            return;

        } else {

            // filter added
            const filteredFinishings = this.finishings().filter(item => item.machineId);

            if (filteredFinishings.length === 0) {
                this.loading.set(false);
                this.notification.set({ type: 'error', message: `No valid job operations to save.` });
                return;
            }

            const cleanJobNo = (this.selectedJobDetails.jobNumber || String(this.selectedJobDetails.id)).trim();

            const jobOperations = filteredFinishings.map((item, index) => {
                const formattedDateTime = item.endDate.replace('T', ' ');

                const groupNo = item.workOrderId
                    ? `GRP-${String(item.workOrderId).trim()}-01`
                    : `GRP-${cleanJobNo}-01`;

                return {
                    stage: item.name,
                    sequence: index + 1,
                    estimatedDateTime: formattedDateTime,
                    jobId: this.selectedJobDetails.id,
                    machineId: item.machineId,
                    estimateTime: item.estimateTime,
                    operationQty: item.operationQuantity,
                    isResorting: 0,
                    isRework: 0,
                    groupNo: groupNo,
                    remarks: item.remarks,
                    jobPassNumber: 1,
                    workOrderId: item.workOrderId || null,
                    workOrderName: item.workOrderName || null,
                    isReprint: 0,
                    reprintReason: "",
                    ncrOperators: [],
                    isFinal: item.isFinal ?? false
                };
            });

            // console.log('op',jobOperations)

            const payload = {
                jobOperations,
                deliverDate: this.selectedDeliverDate,
                isFinal: false
            };

            this.jobOperationService.create(payload).subscribe(
                response => {
                    this.loading.set(false);
                    this.notification.set({ type: 'success', message: `Job operation saved successfully.` });
                    this.jobCardService.triggerRefresh();
                    // console.log('Job Operation saved successfully', response);

                    this.finishings.set([]);
                    this.selectedDeliverDate = '';
                    this.selectedJobDetails = null;
                    this.department_id = null;
                    this.errorMessage.set(null);
                    this.$$showFileUploadModal.set(false);
                    this.Department.set([]);
                },
                error => {
                    this.loading.set(false);
                    this.notification.set({ type: 'error', message: `Failed to save job operation` });
                    console.error('Error saving jobs', error);
                    this.errorMessage.set(`Error saving jobs: ${error.message || 'Unknown error'}`);

                }
            );
        }
    }

    isFormValid(): boolean {
        if (!this.selectedDeliverDate) {
            return false;
        }

        // for (const item of this.finishings()) {
        //     if (!item.departmentId || !item.machineId || !item.endDate) {
        //         return false;
        //     }
        // }
        return true;
    }

    protected formatDateTime(date: string): string {
        return `${date}`;
    }

    onMachineSelect(machineId: number, itemIndex: number) {
        machineId = Number(machineId);

        const updatedFinishings = [...this.finishings()];
        const item = updatedFinishings[itemIndex];
        const selectedMachine = item.availableMachines.find(m => m.machineId === machineId);

        if (selectedMachine) {
            const hourlyOutput = selectedMachine.hourlyOutput;
            const jobCard = this.jobCardGetDTOS()[0];
            const isFlexo = jobCard?.productType?.toLowerCase().includes('flexo');

            const quantity = item.operationQuantity || 0;

            if (hourlyOutput > 0 && quantity >= 0) {
                const additionalTime = isFlexo ? 1 : 0.75;
                const estimateTime = (((quantity / hourlyOutput) + additionalTime).toFixed(2));
                item.estimateTime = Number(estimateTime);
            } else {
                item.estimateTime = null;
            }

            // Use getNextTime to fetch next available time
            this.machineService.getNextAvailableTime(machineId).pipe(
                tap(response => {
                    const nextAvailableTime = response.data.nextAvailableTime;
                    // console.log(nextAvailableTime);

                    item.endDate = this.formatNewDateTime(response.data.nextAvailableTime);
                    // console.log(this.formatNewDateTime(response.data.nextAvailableTime));

                    this.finishings.set(updatedFinishings);
                }),
                catchError(error => {
                    this.notification.set({ type: 'error', message: `Failed to get next available time` });
                    console.error(error);
                    item.estimateTime = null;
                    item.endDate = '';
                    this.finishings.set(updatedFinishings);
                    return of(null);
                })
            ).subscribe();
        } else {
            item.estimateTime = null;
            item.endDate = '';
            this.finishings.set(updatedFinishings);
        }

        item.machineId = machineId;
    }

    onEndDateChange(date: string, index: number): void {
        const updatedFinishings = [...this.finishings()];
        updatedFinishings[index].endDate = date;
        this.finishings.set(updatedFinishings);
    }

    formatNewDateTime(dateStr: string): string {
        return dateStr.slice(0, 16).replace('T', ' ');
    }

    onEstimateTimeChange(estimateTime: number, index: number): void {
        const updatedFinishings = [...this.finishings()];
        updatedFinishings[index].estimateTime = estimateTime >= 0 ? estimateTime : null;
        this.finishings.set(updatedFinishings);
    }

    openFileUploadModal(): void {
        this.modal.$$showFileUploadModal.set(true);
    }

    onFileSelected(event: any): void {
        const file = event.target.files[0];
        if (file) {
            this.selectedFileName = file.name;
            this.selectedFile = file;
            this.errorMessageN = null;

            const fileType = file.name.split('.').pop()?.toLowerCase();
            if (fileType === 'xlsx' || fileType === 'xls' || fileType === 'csv') {
                this.readFile(file);
            } else {
                this.errorMessageN = 'Invalid file type. Only .xlsx, .xls, and .csv files are allowed.';
            }
        }
    }

    private readFile(file: File): void {
        const reader = new FileReader();

        reader.onload = (e: any) => {
            const binaryStr = e.target.result;
            const workbook = XLSX.read(binaryStr, { type: 'binary' });

            const sheetNames = workbook.SheetNames;
            const firstSheet = workbook.Sheets[sheetNames[0]];
            const rawJobData = XLSX.utils.sheet_to_json<any>(firstSheet);

            const jobData: JobCardDTO[] = rawJobData.map(row => ({
                jobNumber: row['JobNo'],
                batchNumber: row['batchNo'],
                productType: row['productTypeName'],
                description: row['jobDescription'],
                quantity: Number(row['jobQuantity']) || 0,
                noOfImpressions: Number(row['impressions']) || 0,
                noOfColour: Number(row['noOfColors']) || 0,
                noOfCuts: Number(row['noOfCuts']) || 0,
                noOfUps: Number(row['noOfUps']) || 0,
                noOfBreakDown: Number(row['jobBreakdownDescription']) || 0,
                reqDate: row['cusReqDate'],
                deliDate: '',
                size: '',
                finishing: (row['productTypeName'] && String(row['productTypeName']).toLowerCase().includes('flexo')) ? (row['finishings'] ? row['finishings'] + ', SLITTING' : 'SLITTING') : row['finishings'],
                artWorkDate: this.formatDate(row['artworkAppDate']),
                customer: row['customer'],
                csReleasedAt: row['csReleasedAt'],
                reelLength: row['reelLength'],
                reelWidth: row['reelWidth'],
                slitWidth: row['slitWidth'],
                cutSheets: row['noOfCutSheets'],
                repeatOrNew: row['repeatOrNew'],
                passCount: row['passCount'],
                remark: row['remark'],
                printMachine: row['machine'],
                isCallout: row['isCallout'],
                salesPerson: row['salesPerson'],
                salesPersonEmail: row['salesPersonEmail']
            }));

            console.log('Transformed job data:', jobData);

            this.saveJobs(jobData);

        };
        reader.readAsBinaryString(file);
    }

    private saveJobs(jobs: JobCardDTO[]): void {
        if (jobs && jobs.length > 0) {
            const payload = {
                jobCards: jobs,
            };
            // console.log(payload)
            this.jobCardService.create(payload).subscribe(
                response => {
                    // console.log('Jobs saved successfully', response);
                    this.jobCardService.triggerRefresh();
                    this.notification.set({ type: 'success', message: `Job Cards Uploaded Successfully!` });
                    this.modal.$$showFileUploadModal.set(false);
                },
                error => {
                    console.error('Error saving jobs', error);
                    this.errorMessageN = `Error saving jobs: ${error.message || 'Unknown error'}`;
                    this.notification.set({ type: 'error', message: `Failed to Upload Job Cards!` });
                }
            );
        } else {
            this.errorMessageN = 'No valid jobs found to save.';
        }
    }

    private formatDate(inputDate: string): string {
        if (!inputDate) return '';

        const match = inputDate.match(/^(\d{4})(\d{2})(\d{2})/);
        if (match) {
            return `${match[1]}-${match[2]}-${match[3]}`;
        }
        return inputDate.split(' ')[0];
    }


}
